import React from 'react';

import type { GmailInviteBatch } from '../services/gmailInviteService';

import './GmailInviteHelp.css';

/** What Google's permission page lists, in its words: Invite all needs every box ticked. */
const GOOGLE_ROWS = [
  'See and download your contacts',
  'See and download contact info automatically saved in your “Other contacts”',
  'Send email on your behalf',
];

const Tick: React.FC = () => (
  <span aria-hidden="true" className="gmail-consent__box">
    <svg viewBox="0 0 12 12"><path d="m2.5 6.2 2.3 2.3 4.7-4.9" /></svg>
  </span>
);

/** The numbers match the steps beside the picture: 2 Select all, 3 Continue. */
const Step: React.FC<{ n: number }> = ({ n }) => <span aria-hidden="true" className="gmail-consent__step">{n}</span>;

/** A small picture of Google's permission page: Select all ticked (step 2), then Continue (step 3). */
const ConsentPicture: React.FC = () => (
  <figure aria-label="Google's permission page: tick Select all, then press Continue" className="gmail-consent"
    role="img">
    <div className="gmail-consent__page">
      <p className="gmail-consent__brand">Sign in with Google</p>
      <p className="gmail-consent__title">8080.AI wants access to your Google Account</p>
      <p className="gmail-consent__label">Select what 8080.AI can access</p>
      <div className="gmail-consent__row is-all is-marked">
        <Step n={2} />
        <span>Select all</span>
        <Tick />
      </div>
      {GOOGLE_ROWS.map((row) => (
        <div className="gmail-consent__row" key={row}><span>{row}</span><Tick /></div>
      ))}
      <div className="gmail-consent__buttons">
        <span>Cancel</span>
        <span className="gmail-consent__continue is-marked"><Step n={3} />Continue</span>
      </div>
    </div>
  </figure>
);

interface PermissionHelpProps {
  onRetry: () => void;
  /** Invite all cannot start now (a batch runs, Google is opening, today's invites are used). */
  disabled: boolean;
}

/**
 * Back from Google without the access Invite all needs (Cancel, or a box left unticked): what to tick there, shown
 * on a picture of Google's page, and Try again.
 */
export const PermissionHelp: React.FC<PermissionHelpProps> = ({ onRetry, disabled }) => (
  <div aria-labelledby="gmail-help-title" className="gmail-help" role="alert">
    <div className="gmail-help__copy">
      <h5 id="gmail-help-title">Oops! Google did not give permission</h5>
      <p>No invites went out.</p>
      <ol className="gmail-help__steps">
        <li>Press <b>Try again</b>.</li>
        <li>On Google’s page, tick <b>Select all</b>.</li>
        <li>Press <b>Continue</b>.</li>
      </ol>
      <button className="refer-way__button gmail-help__retry" disabled={disabled} onClick={onRetry} type="button">
        Try again
      </button>
    </div>
    <ConsentPicture />
  </div>
);

/** An Invite all that finished without inviting anyone. */
export const invitedNoOne = (batch: GmailInviteBatch): boolean => batch.status === 'completed' && batch.invite_count === 0;

const contacts = (count: number): string => `${count.toLocaleString()} ${count === 1 ? 'contact' : 'contacts'}`;

/** No email address to invite: none in the Google account at all, or none that can get one. */
export const NoEmailsFound: React.FC<{ batch: GmailInviteBatch }> = ({ batch }) => (
  <div aria-labelledby="gmail-empty-title" className="gmail-help gmail-help--empty" role="status">
    <div className="gmail-help__copy">
      <h5 id="gmail-empty-title">{batch.contacts_read === 0 ? 'No emails found' : 'No one new to invite'}</h5>
      <p>
        {batch.contacts_read === 0
          ? 'We found no email addresses in your Google contacts. No invites went out.'
          : `None of your ${contacts(batch.contacts_read)} can get an invite. They have no email address, use `
            + '8080.AI already, or opted out.'}
      </p>
      <p className="gmail-help__hint">Add contacts in Google Contacts. Then press Invite all again.</p>
    </div>
  </div>
);
